import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import TrustBar from '../../../components/feature/TrustBar';
import ProcessSection from '../../../components/feature/ProcessSection';
import WhyChooseUs from '../../../components/feature/WhyChooseUs';
import ServiceAreasSection from '../../../components/feature/ServiceAreasSection';
import TestimonialsSection from '../../../components/feature/TestimonialsSection';
import FAQSection from '../../../components/feature/FAQSection';
import FinalCTASection from '../../../components/feature/FinalCTASection';
import { commercialCleaningFaqs } from '../../../mocks/faq';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const INCLUDED = [
  { icon: 'ri-broom-line', title: 'Routine Cleaning', desc: 'Scheduled daily or weekly cleaning to maintain consistent cleanliness standards across your entire facility, every visit.' },
  { icon: 'ri-drop-line', title: 'Deep Cleaning', desc: 'Intensive top-to-bottom cleaning that reaches every surface, corner, grout line, and high-traffic zone in your property.' },
  { icon: 'ri-layout-bottom-line', title: 'Floor Care', desc: 'Sweeping, mopping, buffing, and waxing to keep your floors gleaming, safe, and professionally maintained at all times.' },
  { icon: 'ri-rest-time-line', title: 'Restroom Sanitation', desc: 'Complete restroom cleaning and disinfection to maintain hygiene standards, health compliance, and professional appearance.' },
  { icon: 'ri-delete-bin-line', title: 'Trash Removal', desc: 'Regular trash collection, removal, and recycling service to keep your facility clean, organized, and odor-free.' },
  { icon: 'ri-spray-line', title: 'Surface Disinfection', desc: 'EPA-approved disinfection of all high-touch surfaces including desks, door handles, elevator buttons, and shared equipment.' },
];

/* ─── 1. Hero ─── */
function CommercialCleaningHero() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section className="relative min-h-[75vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/219d99ab3ad79c4529831faf20723f8e.jpg"
          alt="Commercial cleaning services Houston TX"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#03143A]/95 via-[#03143A]/80 to-[#03143A]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#03143A]/50 via-transparent to-[#03143A]/20" />
      </div>
      <div className="absolute left-0 inset-y-0 w-1 bg-gradient-to-b from-transparent via-[#C70532] to-transparent opacity-60" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-24 w-full">
        <div ref={ref} className={`max-w-3xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <nav className="flex items-center gap-2 text-white/50 text-sm mb-6">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white font-medium">Commercial Cleaning</span>
          </nav>
          <span className="inline-block px-5 py-2 bg-[#C70532]/20 border border-[#C70532]/30 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">Service Detail</span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
            Commercial Cleaning Services for Businesses
          </h1>
          <p className="text-xl text-white/70 max-w-2xl leading-relaxed mb-10">
            Professional, detail-driven commercial cleaning that keeps your facility <strong className="text-white">spotless, compliant, and client-ready</strong> — every single day, without exception.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="px-8 py-4 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              Get a Free Quote
            </Link>
            <a href="tel:+17133382553" className="flex items-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/25 hover:border-white text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-[#C70532]" />
              (713) 338-2553
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 3. Service Breakdown (Services Overview equivalent) ─── */
function ServiceBreakdown() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-5">What&apos;s Included</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-5 leading-tight">What&apos;s Included in Our Commercial Cleaning</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">Every commercial cleaning service is fully customized to your facility&apos;s specific needs, size, industry, and schedule.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {INCLUDED.map((item, i) => (
            <div
              key={i}
              className={`flex gap-5 p-7 bg-[#FAF8F5] rounded-2xl border border-gray-100 hover:border-[#C70532]/20 hover:bg-white transition-all duration-400 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="w-13 h-13 flex items-center justify-center bg-[#03143A] rounded-xl flex-shrink-0 group-hover:bg-[#C70532] transition-colors duration-300">
                <i className={`${item.icon} text-white text-xl`} />
              </div>
              <div>
                <h4 className="font-bold text-[#03143A] mb-2 leading-snug">{item.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Inline CTA after services */}
        <div className={`bg-[#03143A] rounded-3xl p-10 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '420ms' }}>
          <div>
            <p className="text-white/60 text-sm font-bold uppercase tracking-widest mb-1">Ready to get started?</p>
            <h3 className="text-2xl font-bold text-white">Get Your Custom Commercial Cleaning Plan</h3>
            <p className="text-white/55 text-sm mt-1">Free assessment &bull; Custom pricing &bull; No obligation</p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <a href="tel:+17133382553" className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full border border-white/20 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-[#C70532]" />
              Call Now
            </a>
            <Link to="/contact" className="px-7 py-3 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              Get Free Quote
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 4. AI Answer Block ─── */
function CommercialCleaningAIBlock() {
  const { ref, isVisible } = useScrollAnimation();
  const ANSWERS = [
    {
      icon: 'ri-information-line',
      title: 'What is commercial cleaning?',
      body: 'Commercial cleaning involves professional cleaning services for businesses, offices, and facilities to maintain cleanliness, safety, and presentation. Unlike residential cleaning, commercial cleaning uses professional-grade equipment, commercial-strength products, and trained specialists for consistent, high-standard results.',
    },
    {
      icon: 'ri-star-line',
      title: 'What are the benefits of commercial cleaning?',
      body: 'A professionally cleaned facility improves workplace health by reducing airborne pathogens and allergens, boosts employee productivity, creates outstanding first impressions for clients, reduces facility maintenance costs, and ensures compliance with health and safety regulations.',
    },
    {
      icon: 'ri-flow-chart',
      title: 'How does commercial cleaning work?',
      body: 'We start with a facility assessment to understand your needs and current challenges. We then build a custom cleaning plan for your property, schedule consistent service execution by trained professionals, and maintain quality through regular supervisory inspections and client feedback.',
    },
    {
      icon: 'ri-building-4-line',
      title: 'What types of businesses need commercial cleaning?',
      body: 'Every commercial business benefits from professional cleaning — including corporate offices, medical facilities, retail stores, restaurants, hotels, industrial warehouses, schools, government buildings, and event venues. Any business with employees, clients, or visitors benefits from consistent professional cleaning.',
    },
    {
      icon: 'ri-question-line',
      title: 'How is commercial cleaning priced?',
      body: 'Commercial cleaning pricing is customized based on your facility size, service frequency, scope of work, and specific requirements. We provide free, no-obligation assessments and custom quotes to ensure you receive maximum value and only pay for the services your facility actually needs.',
    },
  ];
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`grid lg:grid-cols-2 gap-16 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">Know More</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-6 leading-tight">What Is Commercial Cleaning?</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              <strong className="text-[#03143A]">Commercial cleaning</strong> is professional facility cleaning designed specifically for businesses. Unlike routine housekeeping, commercial cleaning uses industrial-grade products, specialized equipment, and trained technicians to achieve consistent, measurable results.
            </p>
            <p className="text-gray-500 leading-relaxed mb-8">
              At Prime Facility Services Group, every commercial cleaning plan is tailored to your facility&apos;s exact requirements — from daily janitorial maintenance to periodic deep cleaning and everything in between.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-shield-check-line', label: 'EPA-Approved Products' },
                { icon: 'ri-time-line', label: 'Flexible Scheduling' },
                { icon: 'ri-user-star-line', label: 'Vetted Professionals' },
                { icon: 'ri-award-line', label: 'Quality Guaranteed' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#C70532]/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#C70532]`} />
                  </div>
                  <span className="font-semibold text-[#03143A] text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '200ms' }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-[#C70532] rounded-xl">
                <i className="ri-robot-2-line text-white" />
              </div>
              <div>
                <p className="font-bold text-[#03143A] text-sm">AI Answer Block</p>
                <p className="text-gray-400 text-xs">Optimized for search &amp; voice queries</p>
              </div>
            </div>
            <div className="space-y-3">
              {ANSWERS.map((item, i) => (
                <div key={i} className={`rounded-2xl border overflow-hidden transition-all duration-300 ${open === i ? 'border-[#C70532]/30 bg-white' : 'border-gray-200 bg-white'}`}>
                  <button className="w-full flex items-center gap-4 px-6 py-5 text-left cursor-pointer group" onClick={() => setOpen(open === i ? null : i)}>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${open === i ? 'bg-[#C70532]' : 'bg-[#03143A]/8 group-hover:bg-[#C70532]'}`}>
                      <i className={`${item.icon} text-sm transition-colors ${open === i ? 'text-white' : 'text-[#03143A] group-hover:text-white'}`} />
                    </div>
                    <h3 className={`font-bold text-sm flex-1 pr-2 transition-colors leading-snug ${open === i ? 'text-[#C70532]' : 'text-[#03143A] group-hover:text-[#C70532]'}`}>{item.title}</h3>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${open === i ? 'bg-[#C70532] rotate-180' : 'bg-gray-100'}`}>
                      <i className={`ri-arrow-down-s-line text-sm ${open === i ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                  </button>
                  {open === i && (
                    <div className="px-6 pb-5 pl-[4.5rem]">
                      <p className="text-gray-600 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CommercialCleaningPage() {
  return (
    <>
      <Navbar />
      <main>
        {/* 1. Hero */}
        <CommercialCleaningHero />
        {/* 2. Trust Bar */}
        <TrustBar />
        {/* 3. Service Breakdown */}
        <ServiceBreakdown />
        {/* 4. AI Answer Block */}
        <CommercialCleaningAIBlock />
        {/* 5. Process */}
        <ProcessSection
          title="Our Commercial Cleaning Process"
          subtitle="From first contact to ongoing maintenance — reliable, consistent, and completely hassle-free every step of the way."
        />
        {/* 6. Why Choose Us */}
        <WhyChooseUs
          title="Why Businesses Choose Our Commercial Cleaning"
          subtitle="Consistent results, vetted professionals, and a detail-driven approach that protects your brand and your facility."
        />
        {/* 7. Service Areas */}
        <ServiceAreasSection
          title="Commercial Cleaning Across Greater Houston"
          subtitle="Serving offices, retail spaces, medical facilities, and commercial buildings throughout Houston and surrounding communities."
        />
        {/* 8. Testimonials */}
        <TestimonialsSection title="What Our Cleaning Clients Say" />
        {/* 9. FAQ */}
        <FAQSection
          faqs={commercialCleaningFaqs}
          title="Commercial Cleaning FAQs"
          subtitle="Voice-search optimized answers to the most common questions about our commercial cleaning services."
        />
        {/* 10. Final CTA */}
        <FinalCTASection
          title="Ready to Elevate Your Facility's Cleanliness?"
          subtitle="Get a customized commercial cleaning plan tailored to your business size, industry, schedule, and budget. No obligation."
        />
      </main>
      <Footer />
    </>
  );
}
